import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useArc } from '../context/ArcContext';
import { localDateKey } from '../lib/dates';
import { useLocalDateKey } from '../hooks/useLocalDateKey';
import {
  Home, Utensils, Bus, Plug, Shield, Repeat, MoreHorizontal,
  PiggyBank, Rocket, Info, Check, CheckCircle, X
} from 'lucide-react';

// ─── Config ───────────────────────────────────────────────────────────────────

const EXPENSE_FIELDS = [
  { key: 'rent',          label: 'Rent / Housing',  icon: Home,          color: '#e05252' },
  { key: 'food',          label: 'Food',            icon: Utensils,      color: '#1abc9c' },
  { key: 'transport',     label: 'Transport',       icon: Bus,           color: '#4a9cc4' },
  { key: 'utilities',     label: 'Utilities',       icon: Plug,          color: '#f39c12' },
  { key: 'insurance',     label: 'Insurance',       icon: Shield,        color: '#9b59b6' },
  { key: 'subscriptions', label: 'Subscriptions',   icon: Repeat,        color: '#C7A46D' },
  { key: 'other',         label: 'Other',           icon: MoreHorizontal,color: '#7f8c8d' },
];

// Zone Gate definitions — two zones, each with their requirements
const ZONE_GATES = {
  2: [
    { key: 'foundation_covered',  label: 'Monthly income covers monthly foundation' },
    { key: 'emergency_3mo',       label: '3-month emergency reserve built in cash' },
    { key: 'first_productised',   label: 'One productised income line installed and generating revenue' },
  ],
  3: [
    { key: 'first_delegated_hire', label: 'First delegated hire or role owner in place' },
    { key: 'asset_income_live',    label: 'Asset-backed income stream live (rental, equity, royalty)' },
  ],
};
// Flat list for legacy compatibility
const ZONE_GATE_DEFS = [...ZONE_GATES[2], ...ZONE_GATES[3]];

// View → months window
const VIEW_MONTHS = { weekly: 2, monthly: 12, yearly: 60 };

// Phase 7 P4: Foundation line = Silver (#C0C0C0) — permanently distinct from engine income line
const FOUNDATION_COLOR = '#C0C0C0';

// ─── Coverage Ring ────────────────────────────────────────────────────────────

function CoverageRing({ pct }) {
  const size = 180, r = 76, c = 2 * Math.PI * r;
  const dash = (Math.min(Math.min(pct, 200), 100) / 100) * c;
  const ringColor = pct >= 150 ? '#1D9E75' : pct >= 100 ? '#C7A46D' : 'var(--el)';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="10" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={ringColor} strokeWidth="10" fill="none"
          strokeDasharray={c} strokeDashoffset={c - dash} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 600ms ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="eyebrow">Coverage</div>
        <div className="font-head text-[34px] mt-0.5" style={{ color: ringColor }}>{pct}%</div>
        <div className="font-data text-[11px] text-white/45">of foundation</div>
      </div>
    </div>
  );
}

// ─── Zone Chart — Projected vs Actual ────────────────────────────────────────
// Both income AND foundation are moving lines — each check-in records current
// values of both, so they go up and down over time.
//
// Income line:      engine colour, bezier through check-in dots + live dot today
// Foundation line:  silver, same treatment — expenses change too
// Projected income: dashed silver, extrapolated forward from income trend
// Zone 2 / 3 gate:  vertical amber / green dotted lines at projected crossing dates
// Zone thresholds:  faint dashed horizontals computed from live foundation value

function ZoneChart({ log, foundation, income, engineColor, view }) {
  const ref = useRef(null);
  const months = VIEW_MONTHS[view] || 12;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.clearRect(0, 0, W, H);

    const padL = 58, padR = 24, padT = 28, padB = 30;
    const innerW = W - padL - padR, innerH = H - padT - padB;

    const now = new Date();

    // ── Parse historical check-ins ────────────────────────────────────────────
    const rawCheckIns = [...(log || [])]
      .map(e => {
        const ds = e.date || (e.ym ? e.ym + '-15' : null);
        if (!ds) return null;
        const dateObj = new Date(ds);
        return { dateObj, f: Number(e.foundation || 0), inc: Number(e.income || 0) };
      })
      .filter(Boolean)
      .filter(e => e.dateObj <= now)
      .sort((a, b) => a.dateObj - b.dateObj);
    const firstCheckIn = rawCheckIns[0]?.dateObj || now;
    const startDate = new Date(firstCheckIn);
    startDate.setHours(0, 0, 0, 0);
    const futureMs = months * 0.40 * 30 * 86400000;
    const endDate = new Date(now.getTime() + futureMs);
    const totalMs = Math.max(1, endDate.getTime() - startDate.getTime());
    const tx = d => padL + Math.max(0, Math.min(1, (d.getTime() - startDate.getTime()) / totalMs)) * innerW;
    const txNow = tx(now);
    const todayKey = localDateKey(now);
    const checkIns = rawCheckIns.filter(e => e.dateObj >= startDate && localDateKey(e.dateObj) !== todayKey);

    // Live snapshot at today (always present — most recent values)
    const live = { dateObj: now, inc: Number(income || 0), f: Number(foundation || 0) };

    // Full actual series: historical check-ins + today's live values
    const incSeries = [...checkIns, live];
    const fndSeries = [...checkIns, live];

    // ── Zone targets (based on live foundation) ───────────────────────────────
    const liveF = live.f;
    const z2Threshold = liveF * 2.0;   // Zone 2: income ≥ 2× foundation
    const z3Threshold = liveF * 4.0;   // Zone 3: income ≥ 4× foundation

    // ── Income projection forward ─────────────────────────────────────────────
    let projPts = [];
    let z2Cross = null, z3Cross = null;
    {
      const first    = checkIns.length > 0 ? checkIns[0] : live;
      const latest   = live;
      const elapsed  = Math.max(1, (now - first.dateObj) / 86400000);
      const rawGrowth = (latest.inc - first.inc) / elapsed;
      // Use actual growth if positive, otherwise assume gentle 1.5% / month floor
      const dayGrowth = rawGrowth > 0 ? rawGrowth : (latest.inc * 0.015) / 30;

      projPts = [{ dateObj: now, inc: latest.inc }];
      const step = 14; // days
      let d = new Date(now.getTime() + step * 86400000);
      while (d <= endDate) {
        const dAhead = (d - now) / 86400000;
        const projInc = latest.inc + dayGrowth * dAhead;
        projPts.push({ dateObj: new Date(d), inc: projInc });
        if (!z2Cross && projInc >= z2Threshold) z2Cross = new Date(d);
        if (!z3Cross && projInc >= z3Threshold) z3Cross = new Date(d);
        d = new Date(d.getTime() + step * 86400000);
      }
      if (!z2Cross && latest.inc >= z2Threshold) z2Cross = now;
      if (!z3Cross && latest.inc >= z3Threshold) z3Cross = now;
    }

    // ── Y scale — fits both lines + projected + thresholds ───────────────────
    const allVals = [
      ...incSeries.map(p => p.inc),
      ...fndSeries.map(p => p.f),
      ...projPts.map(p => p.inc),
      z2Threshold, z3Threshold, 500,
    ].filter(v => v > 0);
    const yMax = Math.max(...allVals) * 1.20;
    const ty = v => padT + innerH - (Math.max(0, v) / yMax) * innerH;

    // ── Background grid ───────────────────────────────────────────────────────
    ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 0.75;
    for (let i = 0; i <= 5; i++) {
      const y = padT + (i / 5) * innerH;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
    }
    const monthSpan = Math.max(1, (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth());
    for (let mo = 0; mo <= monthSpan; mo++) {
      const d = new Date(startDate.getFullYear(), startDate.getMonth() + mo, 1);
      const x = tx(d);
      if (x >= padL && x <= W - padR) {
        ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      }
    }

    // ── Zone threshold dashed horizontals ─────────────────────────────────────
    if (liveF > 0) {
      const hRef = (val, color, label) => {
        if (val <= 0 || val > yMax) return;
        ctx.setLineDash([4, 6]); ctx.strokeStyle = color; ctx.lineWidth = 0.9;
        ctx.beginPath(); ctx.moveTo(padL, ty(val)); ctx.lineTo(W - padR, ty(val)); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = color; ctx.font = '600 9px "Raleway", sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(label, padL + 5, ty(val) - 3);
      };
      hRef(z2Threshold, 'rgba(199,164,109,0.55)', `Z2 income  $${Math.round(z2Threshold).toLocaleString()}`);
      hRef(z3Threshold, 'rgba(29,158,117,0.55)',  `Z3 income  $${Math.round(z3Threshold).toLocaleString()}`);
    }

    // ── "Now" divider ─────────────────────────────────────────────────────────
    ctx.setLineDash([3, 4]); ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(txNow, padT); ctx.lineTo(txNow, H - padB); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.30)'; ctx.font = '600 8px "Raleway", sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('NOW', txNow, padT - 8);

    // ── Zone gate vertical lines ──────────────────────────────────────────────
    const vGate = (date, color, label) => {
      if (!date) return;
      const x = tx(date);
      if (x <= txNow || x > W - padR) return; // only in future side
      ctx.setLineDash([3, 5]); ctx.strokeStyle = color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color; ctx.font = '700 8.5px "Raleway", sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(label, x, padT - 8);
      // small diamond
      ctx.beginPath(); ctx.moveTo(x, padT-2); ctx.lineTo(x+4, padT+5); ctx.lineTo(x, padT+12); ctx.lineTo(x-4, padT+5); ctx.closePath();
      ctx.fillStyle = color; ctx.fill();
    };
    vGate(z2Cross, 'rgba(199,164,109,0.90)', 'Z2 point');
    vGate(z3Cross, 'rgba(29,158,117,0.90)',  'Z3 point');

    // ── Projected income line (dashed, silver) ────────────────────────────────
    if (projPts.length > 1) {
      ctx.setLineDash([7, 5]);
      ctx.strokeStyle = 'rgba(192,192,192,0.40)'; ctx.lineWidth = 1.5;
      ctx.beginPath();
      projPts.forEach((p, i) => {
        i === 0 ? ctx.moveTo(tx(p.dateObj), ty(p.inc))
                : ctx.lineTo(tx(p.dateObj), ty(p.inc));
      });
      ctx.stroke(); ctx.setLineDash([]);
      const lp = projPts[projPts.length - 1];
      ctx.fillStyle = 'rgba(192,192,192,0.40)';
      ctx.font = '600 8px "Raleway", sans-serif'; ctx.textAlign = 'right';
      ctx.fillText('PROJ', Math.min(tx(lp.dateObj), W - padR - 2), ty(lp.inc) - 5);
    }

    // ── Helper: draw a bezier line series ────────────────────────────────────
    const drawSeries = (pts, key, color, fillUnder, dotR, tag) => {
      if (pts.length === 0) return;

      // Single point → just a dot, no line
      if (pts.length === 1) {
        ctx.beginPath(); ctx.arc(tx(pts[0].dateObj), ty(pts[0][key]), dotR + 1, 0, Math.PI * 2);
        ctx.fillStyle = '#fff'; ctx.fill();
        ctx.strokeStyle = color; ctx.lineWidth = 1.8; ctx.stroke();
        return;
      }

      // Gradient fill under income line only
      if (fillUnder) {
        const grd = ctx.createLinearGradient(0, padT, 0, H - padB);
        grd.addColorStop(0, color + '38'); grd.addColorStop(1, color + '00');
        ctx.beginPath();
        pts.forEach((p, i) => {
          const x = tx(p.dateObj), y = ty(p[key]);
          if (i === 0) { ctx.moveTo(x, y); return; }
          const prev = pts[i - 1];
          const cx = (tx(prev.dateObj) + x) / 2;
          ctx.bezierCurveTo(cx, ty(prev[key]), cx, y, x, y);
        });
        const last = pts[pts.length - 1];
        ctx.lineTo(tx(last.dateObj), H - padB);
        ctx.lineTo(tx(pts[0].dateObj), H - padB);
        ctx.closePath(); ctx.fillStyle = grd; ctx.fill();
      }

      // Main line
      ctx.beginPath();
      pts.forEach((p, i) => {
        const x = tx(p.dateObj), y = ty(p[key]);
        if (i === 0) { ctx.moveTo(x, y); return; }
        const prev = pts[i - 1];
        const cx = (tx(prev.dateObj) + x) / 2;
        ctx.bezierCurveTo(cx, ty(prev[key]), cx, y, x, y);
      });
      ctx.strokeStyle = color; ctx.lineWidth = 2.2;
      ctx.shadowColor = color + 'aa'; ctx.shadowBlur = fillUnder ? 12 : 5;
      ctx.stroke(); ctx.shadowBlur = 0;

      // Historical check-in dots (not the live point)
      pts.slice(0, -1).forEach(p => {
        ctx.beginPath(); ctx.arc(tx(p.dateObj), ty(p[key]), dotR, 0, Math.PI * 2);
        ctx.fillStyle = color; ctx.fill();
        ctx.strokeStyle = '#0B0A08'; ctx.lineWidth = 1.1; ctx.stroke();
      });

      // Live dot (white fill + colour ring + glow)
      const lp = pts[pts.length - 1];
      ctx.beginPath(); ctx.arc(tx(lp.dateObj), ty(lp[key]), dotR + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff'; ctx.fill();
      ctx.strokeStyle = color; ctx.lineWidth = 2;
      ctx.shadowColor = color; ctx.shadowBlur = 10;
      ctx.stroke(); ctx.shadowBlur = 0;

      // Line tag label
      if (tag) {
        ctx.fillStyle = color; ctx.font = '600 8px "Raleway", sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(tag, tx(lp.dateObj) - 38, ty(lp[key]) + 13);
      }
    };

    // Foundation first (behind income)
    drawSeries(fndSeries, 'f',   FOUNDATION_COLOR, false, 3.5, 'FOUNDATION');
    // Income on top
    drawSeries(incSeries, 'inc', engineColor,       true,  4.5, 'INCOME');

    // ── X-axis labels ─────────────────────────────────────────────────────────
    ctx.fillStyle = 'rgba(255,255,255,0.32)'; ctx.font = '600 9px "Raleway", sans-serif'; ctx.textAlign = 'center';
    const MNAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const labelCount = view === 'weekly' ? 8 : view === 'yearly' ? 6 : 7;
    for (let i = 0; i <= labelCount; i++) {
      const frac = i / labelCount;
      const d    = new Date(startDate.getTime() + frac * totalMs);
      const x    = tx(d);
      if (x < padL - 4 || x > W - padR + 4) continue;
      const lab = `${MNAMES[d.getMonth()]}${d > now ? ` '${String(d.getFullYear()).slice(2)}` : ''}`;
      ctx.fillText(lab, x, H - 10);
    }

    // ── Y-axis label ──────────────────────────────────────────────────────────
    ctx.save(); ctx.translate(13, padT + innerH / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = 'rgba(255,255,255,0.30)'; ctx.font = '600 9px "Raleway", sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('MONTHLY $', 0, 0); ctx.restore();

  }, [log, foundation, income, engineColor, view, months]);

  return <canvas ref={ref} style={{ width: '100%', height: 320, display: 'block' }} />;
}

// ─── Zone Gate Modal (zone = 2 or 3) ─────────────────────────────────────────

function ZoneGateModal({ zone, zoneGates, onToggle, onClose }) {
  const gates   = ZONE_GATES[zone] || [];
  const done    = gates.filter(g => zoneGates[g.key]).length;
  const accent  = zone === 2 ? '#C7A46D' : '#1D9E75';
  const accentA = zone === 2 ? 'rgba(199,164,109,' : 'rgba(29,158,117,';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-5" onClick={onClose}>
      <div className="absolute inset-0 bg-[#050403]/80 backdrop-blur-sm" />
      <div
        className="relative panel-strong p-6 md:p-8 max-w-[500px] w-full rounded-2xl fade-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-data tracking-[0.16em] uppercase mb-3 border"
              style={{ color: accent, borderColor: accent + '55', background: accent + '14' }}
            >
              Zone {zone} Gate
            </span>
            <h2 className="font-head text-[21px] text-white">
              {zone === 2 ? 'Reach financial self-sufficiency' : 'Install leverage and asset income'}
            </h2>
            <p className="text-white/45 text-[13px] mt-1.5 leading-[1.6]">
              {done}/{gates.length} cleared. Click any item to mark it done.
            </p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors ml-3 mt-1 shrink-0">
            <X size={17} />
          </button>
        </div>

        {/* Checklist */}
        <div className="space-y-2">
          {gates.map((gate, idx) => {
            const isDone = !!zoneGates[gate.key];
            return (
              <button
                key={gate.key}
                onClick={() => onToggle(gate.key)}
                className="w-full flex items-start gap-3 px-4 py-3.5 rounded-xl border text-left transition-colors"
                style={{
                  borderColor: isDone ? accentA + '0.5)' : 'rgba(255,255,255,0.07)',
                  background:  isDone ? accentA + '0.07)' : 'rgba(255,255,255,0.02)',
                }}
              >
                <span
                  className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 font-data text-[10px]"
                  style={{
                    background: isDone ? accent : 'rgba(255,255,255,0.05)',
                    border: `1.5px solid ${isDone ? accent : 'rgba(255,255,255,0.15)'}`,
                    color: isDone ? '#030508' : 'rgba(255,255,255,0.35)',
                  }}
                >
                  {isDone ? <Check size={11} strokeWidth={3} /> : idx + 1}
                </span>
                <div className="flex-1">
                  <div
                    className="text-[13.5px] leading-[1.55]"
                    style={{
                      color: isDone ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.88)',
                      textDecoration: isDone ? 'line-through' : 'none',
                    }}
                  >
                    {gate.label}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 px-4 py-3 rounded-lg border border-white/5 bg-white/[0.015] text-[11px] text-white/35 leading-[1.7]">
          Use these gates as readiness markers. Clear them only when they are true.
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Financials() {
  const { state, update, arc, engine, earnBadge } = useArc();
  const dateNow = useLocalDateKey();
  const [view, setView]           = useState('monthly'); // weekly | monthly | yearly
  const [showGates, setShowGates] = useState(null); // null | 2 | 3
  const [justChecked, setJustChecked] = useState(false);

  const setExp  = (k, v) => update({ financials: { ...state.financials, expenses: { ...state.financials.expenses, [k]: Number(v||0) } } });
  const setInc  = (k, v) => update({ financials: { ...state.financials, [k]: Number(v||0) } });
  const setSplit = reserve => update({ financials: { ...state.financials, surplusSplit: { reserve, reinvest: 100 - reserve } } });
  const toggleGate = key => update({ zoneGates: { ...state.zoneGates, [key]: !state.zoneGates[key] } });

  const foundation = useMemo(() =>
    Object.values(state.financials.expenses).reduce((a, b) => a + Number(b||0), 0),
  [state.financials.expenses]);

  const income = useMemo(() =>
    Number(state.financials.primaryIncome||0) + Number(state.financials.secondaryIncome||0),
  [state.financials]);

  const coverage    = foundation > 0 ? Math.round((income / foundation) * 100) : 0;
  const surplus     = Math.max(0, income - foundation);
  const split       = state.financials.surplusSplit || { reserve: 60, reinvest: 40 };
  const reserveAmt  = Math.round(surplus * split.reserve / 100);
  const reinvestAmt = surplus - reserveAmt;

  const log        = useMemo(() => state.financialLog || [], [state.financialLog]);
  const sortedLog  = useMemo(() => [...log].sort((a,b) => (a.ts||'').localeCompare(b.ts||'')), [log]);
  const lastEntry  = sortedLog[sortedLog.length - 1];
  const daysSince  = lastEntry?.ts
    ? Math.floor((Date.now() - new Date(lastEntry.ts).getTime()) / 86400000)
    : Infinity;
  const checkedRecently = daysSince < 14;
  const daysUntilNext   = Math.max(0, 14 - daysSince);
  const ymNow    = dateNow.slice(0, 7);

  const checkIn = () => {
    const next = log
      .filter(e => (e.date || e.ym) !== dateNow)
      .concat([{ date: dateNow, ym: ymNow, foundation, income, ts: new Date().toISOString() }])
      .sort((a,b) => (a.date||a.ym||'').localeCompare(b.date||b.ym||''));
    update({ financialLog: next });
    earnBadge('financial_checkin', 'First Financial Check-in');
    setJustChecked(true);
    setTimeout(() => setJustChecked(false), 1800);
  };

  let zone    = 'Zone 1: Foundation not yet covered';
  let insight = 'Highest-leverage next move: reduce one recurring expense, or install one income line that crosses 100% coverage.';
  if (coverage >= 150) {
    zone    = 'Zone 2: Capital is no longer the constraint';
    insight = 'Time is now the constraint, not money. Delegating one function that consumes deep-work hours is the conventional next step.';
  } else if (coverage >= 100) {
    zone    = 'Zone 1 Late: Stable, leverage not yet installed';
    insight = 'Coverage is met but not yet safe. Building a 3-month reserve before installing leverage is the conventional next step.';
  }

  const segments = EXPENSE_FIELDS
    .map(f => ({ key: f.key, label: f.label, color: f.color, val: Number(state.financials.expenses[f.key]||0) }))
    .filter(s => s.val > 0);
  const segTotal    = segments.reduce((a,b) => a + b.val, 0) || 1;
  const engineColor = engine?.light || '#C7A46D';
  const gatesDone   = ZONE_GATE_DEFS.filter(g => !!state.zoneGates[g.key]).length;
  const z2Done      = ZONE_GATES[2].filter(g => !!state.zoneGates[g.key]).length;
  const z3Done      = ZONE_GATES[3].filter(g => !!state.zoneGates[g.key]).length;

  return (
    <div className="fade-up space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-[28px] md:text-[34px] text-white/95">Financials</h1>
          <p className="text-white/60 text-[14px] mt-1 leading-[1.7] max-w-[680px]">
            Foundation is your monthly survival cost. Coverage shows whether income covers it. Zone gates are milestones you only mark when they are true.
          </p>
        </div>
        <span className="chip" style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
          <Info size={12}/> Zone tracking
        </span>
      </div>

      {/* Top row: ring + KPIs */}
      <div className="grid lg:grid-cols-[260px_1fr] gap-5 items-stretch">
        <div className="panel-strong p-6 flex flex-col items-center justify-center text-center">
          <CoverageRing pct={coverage} />
          <div className="mt-5 grid grid-cols-2 gap-3 w-full">
            <Stat label="Foundation" value={`$${foundation.toLocaleString()}`} />
            <Stat label="Income"     value={`$${income.toLocaleString()}`}     />
          </div>
        </div>

        <div className="space-y-4 min-w-0">
          {/* Foundation breakdown bar */}
          <div className="panel p-5 min-w-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="rail-left font-head text-[15px]">Foundation breakdown</div>
              <div className="font-data text-[12px] text-white/55">${foundation.toLocaleString()} / mo</div>
            </div>
            <div className="mt-3 h-3 rounded-full overflow-hidden bg-white/5 flex">
              {segments.map(s => (
                <div key={s.key} title={`${s.label} · $${s.val}`} style={{ width:`${(s.val/segTotal)*100}%`, background: s.color }} />
              ))}
              {segments.length === 0 && <div className="w-full" style={{ background: 'rgba(255,255,255,0.04)' }} />}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
              {segments.length === 0
                ? <div className="text-white/45 text-[12.5px]">Add at least one expense below to see the breakdown.</div>
                : segments.map(s => (
                    <div key={s.key} className="flex items-center gap-2 text-[12.5px] text-white/75">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                      {s.label} <span className="font-data text-white/45">${s.val.toLocaleString()}</span>
                    </div>
                  ))
              }
            </div>
          </div>

          {/* Surplus allocator */}
          <div className="panel p-5 min-w-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="rail-left font-head text-[15px]">Surplus allocator</div>
              <div className="font-data text-[12px] text-white/55">Surplus ${surplus.toLocaleString()} / mo</div>
            </div>
            <input
              type="range" min="0" max="100" step="5"
              value={split.reserve} onChange={e => setSplit(Number(e.target.value))}
              className="mt-4 w-full accent-[var(--el)]"
            />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="px-3 py-3 rounded-lg border border-white/6 bg-white/2">
                <div className="flex items-center gap-2 eyebrow"><PiggyBank size={12}/> Reserve {split.reserve}%</div>
                <div className="font-data text-[20px] mt-0.5" style={{ color: '#1D9E75' }}>${reserveAmt.toLocaleString()}</div>
              </div>
              <div className="px-3 py-3 rounded-lg border border-white/6 bg-white/2">
                <div className="flex items-center gap-2 eyebrow"><Rocket size={12}/> Reinvest {split.reinvest}%</div>
                <div className="font-data text-[20px] mt-0.5" style={{ color: '#C7A46D' }}>${reinvestAmt.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zone Progress Chart */}
      <div className="panel-strong p-5 md:p-6">
        {/* Chart header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="rail-left font-head text-[16px] text-white/90">Zone Progress</div>
            <div className="text-white/55 text-[13px] mt-1 leading-[1.6]">
              Both income and expenses move. Check in every two weeks. Each dot shows whether your financial base is getting stronger, weaker, or ready for leverage.
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11.5px] flex-wrap">
            <Legend stroke={engineColor}       label="Income"      />
            <Legend stroke={FOUNDATION_COLOR}  label="Foundation"  />
            <Legend stroke="rgba(192,192,192,0.5)" dashed label="Projected" />
          </div>
        </div>

        {/* Zone gate trigger buttons */}
        <div className="flex gap-2 mb-3 flex-wrap">
          <button
            onClick={() => setShowGates(2)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] font-data tracking-[0.1em] uppercase transition-colors"
            style={{ borderColor: 'rgba(199,164,109,0.45)', background: 'rgba(199,164,109,0.07)', color: '#C7A46D' }}
          >
            Zone 2 Gates
            <span className="opacity-60">({z2Done}/{ZONE_GATES[2].length})</span>
          </button>
          <button
            onClick={() => setShowGates(3)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] font-data tracking-[0.1em] uppercase transition-colors"
            style={{ borderColor: 'rgba(29,158,117,0.45)', background: 'rgba(29,158,117,0.07)', color: '#1D9E75' }}
          >
            Zone 3 Gates
            <span className="opacity-60">({z3Done}/{ZONE_GATES[3].length})</span>
          </button>
        </div>
        <div className="mb-3 px-4 py-3 rounded-lg border border-white/6 bg-white/[0.02] text-[12.5px] text-white/50 leading-[1.7]">
          Foundation is what life costs each month. Income is what you bring in now, plus the projected path if it keeps improving. Gates are manual milestones you tick off when they are real, such as cash reserves, productised income, delegated help, or asset income.
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          {/* Check-in button */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={checkIn}
              disabled={checkedRecently && !justChecked}
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] tracking-[0.18em] uppercase font-bold rounded-md transition-all"
              style={{
                background: checkedRecently ? 'rgba(29,158,117,0.12)' : 'linear-gradient(135deg,#C7A46D,#B5925F)',
                color:  checkedRecently ? '#1D9E75' : '#0B0A08',
                border: checkedRecently ? '1px solid rgba(29,158,117,0.4)' : 'none',
                cursor: (checkedRecently && !justChecked) ? 'default' : 'pointer',
              }}
              title={checkedRecently ? `Next check-in in ${daysUntilNext} day${daysUntilNext===1?'':'s'}` : undefined}
            >
              {justChecked
                ? <><Check size={13}/> Logged {dateNow}</>
                : checkedRecently
                  ? <><Check size={13}/> Next in {daysUntilNext}d</>
                  : <><CheckCircle size={13}/> Check in: {dateNow}</>
              }
            </button>
            {checkedRecently && !justChecked && (
              <span className="font-data text-[11px] text-white/35">
                ({daysUntilNext} day{daysUntilNext===1?'':'s'} until next)
              </span>
            )}
          </div>

          {/* Weekly / Monthly / Yearly toggle */}
          <div className="inline-flex items-center rounded-md p-0.5 bg-white/3 border border-white/8">
            {(['weekly','monthly','yearly']).map(v => {
              const active = view === v;
              return (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className="px-3 py-1.5 rounded text-[11px] font-data tracking-wider uppercase transition-colors capitalize"
                  style={{
                    background: active ? 'var(--el)' : 'transparent',
                    color:      active ? '#0B0A08'   : 'rgba(255,255,255,0.5)',
                    fontWeight: active ? 700         : 500,
                  }}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </div>

        <ZoneChart
          log={log}
          foundation={foundation}
          income={income}
          engineColor={engineColor}
          view={view}
        />

        {/* Chart footer */}
        <div className="mt-2 font-data text-[10px] tracking-wider text-white/35">
          {log.length === 0
            ? 'No check-ins yet. Click Check in to plant your first dot.'
            : `${log.length} check-in${log.length===1?'':'s'} · Latest: ${lastEntry?.date||lastEntry?.ym} · Next: ${checkedRecently ? `in ${daysUntilNext}d` : 'now'}`
          }
        </div>

        {/* Zone insight card */}
        <div
          className="mt-3 px-4 py-3 rounded-lg border"
          style={{
            borderColor: 'color-mix(in srgb, var(--el) 30%, rgba(255,255,255,0.07))',
            background:  'color-mix(in srgb, var(--el) 4%, transparent)',
          }}
        >
          <div className="font-head text-[14px]" style={{ color: 'var(--el)' }}>{zone}</div>
          <div className="text-[13px] text-white/70 mt-1 leading-[1.7]">{insight}</div>
          <div className="text-[12px] text-white/35 mt-1">
            As {arc?.key || 'you'}, this is the next move within your Arc's logic. {gatesDone}/{ZONE_GATE_DEFS.length} milestones cleared.
          </div>
        </div>
      </div>

      {/* Input forms */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="panel p-5 min-w-0">
          <div className="rail-left font-head text-[15px] text-white">Foundation Expenses</div>
          <div className="mt-4 space-y-3">
            {EXPENSE_FIELDS.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.key} className="grid grid-cols-[24px_minmax(0,1fr)_140px] items-center gap-3">
                  <Icon size={14} style={{ color: f.color }} />
                  <div className="text-white/80 text-[13.5px] truncate">{f.label}</div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 font-data text-[12px] text-white/40">$</span>
                    <input
                      className="input pl-6 font-data"
                      value={state.financials.expenses[f.key] || ''}
                      onChange={e => setExp(f.key, e.target.value)}
                      inputMode="numeric" placeholder="0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel p-5 min-w-0">
          <div className="rail-left font-head text-[15px] text-white">Income</div>
          <div className="mt-4 space-y-3">
            <IncomeRow
              label="Primary income (salary, retainer, main client)"
              value={state.financials.primaryIncome}
              onChange={v => setInc('primaryIncome', v)}
            />
            <IncomeRow
              label="Secondary income (side clients, freelance, passive)"
              value={state.financials.secondaryIncome}
              onChange={v => setInc('secondaryIncome', v)}
            />
          </div>
          <div className="mt-4 px-4 py-3 rounded-lg border border-white/6 bg-white/2 text-[12.5px] text-white/50 leading-[1.7]">
            Treat these zones as Life OS markers, not financial advice. They help you see when your base is covered and when leverage becomes realistic. Consult a licensed advisor for material decisions.
          </div>
        </div>
      </div>

      {/* Zone Gate Modal */}
      {showGates && (
        <ZoneGateModal
          zone={showGates}
          zoneGates={state.zoneGates}
          onToggle={toggleGate}
          onClose={() => setShowGates(null)}
        />
      )}
    </div>
  );
}

// ─── Helper components ────────────────────────────────────────────────────────

function Stat({ label, value }) {
  return (
    <div className="px-3 py-3 rounded-lg border border-white/6 bg-white/2 text-center">
      <div className="eyebrow">{label}</div>
      <div className="font-data text-[18px] mt-0.5 text-white">{value}</div>
    </div>
  );
}

function IncomeRow({ label, value, onChange }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_160px] items-center gap-3">
      <div className="text-white/80 text-[13.5px]">{label}</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-data text-[12px] text-white/40">$</span>
        <input
          className="input pl-6 font-data"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          inputMode="numeric" placeholder="0"
        />
      </div>
    </div>
  );
}

function Legend({ stroke, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-white/60 text-[11px]">
      <span className="inline-block" style={{
        width: 14,
        height: dashed ? 0 : 8,
        borderTop: dashed ? `2px dashed ${stroke}` : 'none',
        background: dashed ? 'transparent' : stroke,
        borderRadius: dashed ? 0 : 999,
      }} />
      {label}
    </span>
  );
}
